import type { ComponentType } from "react"
import React, { useMemo, useRef } from "react"
import type ReactQuillType from "react-quill-new"
import dynamic from "next/dynamic"

import { Box } from "@chakra-ui/react"

import type Quill from "quill"

import { LoadingAnimation } from "@supliful/common"

import type { EditorProps } from "./Editor"

import "react-quill-new/dist/quill.snow.css"

const ReactQuill = dynamic(
  async () => {
    const module = await import("react-quill-new")
    const ReactQuill = module.default

    return ({
      forwardedRef,
      children: _children,
      ...rest
    }: ReactQuillType.ReactQuillProps & {
      forwardedRef?: any
    }) => <ReactQuill ref={forwardedRef} {...rest} />
  },
  {
    ssr: false,
    loading: () => <LoadingAnimation />,
  },
)

type HMTLEditorProps = EditorProps<string>

export const HTMLEditor = ({ value, onValueChange }: HMTLEditorProps) => {
  const quillRef = useRef<typeof ReactQuill>(null)
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "strike", "link"],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["image"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [],
  )

  function imageHandler() {
    if (!quillRef.current) return

    const reference =
      quillRef.current as ComponentType<ReactQuillType.ReactQuillProps> & {
        getEditor: () => Quill
      }
    const editor = reference.getEditor()
    const range = editor.getSelection()
    const value = prompt("Please enter the image URL")
    if (value && range) {
      editor.insertEmbed(range.index, "image", value, "user")
    }
  }

  return (
    <Box
      w="full"
      sx={{
        ".ql-container": {
          fontFamily: "var(--chakra-fonts-body)",
          fontSize: "var(--chakra-fontSizes-md)",
        },
      }}
    >
      <ReactQuill
        forwardedRef={quillRef}
        theme="snow"
        defaultValue={value}
        onChange={onValueChange}
        modules={modules}
      />
    </Box>
  )
}
